import { useCallback, useMemo } from 'react';
import useFetchActionbarAuth from 'src/hooks/useFetchActionbarAuth';
import { useTQC2020_R01Store } from './Store_TQC2020_R01';
import { TabType } from './Store_TQC2020_R01';
import { getDateProductColumns, getItemProductColumns, getWorkplaceProductColumns } from './components/ColumnDefinitions';
import { showSuccess, ShowSuccessFetch, showWarning } from 'src/utils/toast/toastUtil';
import { downloadSettingExcel } from 'src/utils/blob/blobUtil';
import { exportTQC2020_R01Excel } from './Api_TQC2020_R01';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';

// 훅으로 전환: 컨테이너에서 정상적인 훅 호출로 핸들러를 수급
interface WMS2010ContentProps {
    setMessage: (message: string) => void;
}

export const Content_TQC2020_R01 = (props: WMS2010ContentProps) => {
    const { setMessage } = props;
    
    // 권한 체크
    useFetchActionbarAuth('TQC2020_R01');

    const { 
        TQC2020_R01Filter, 
        activeTab, 
        tabData, 
        setActiveTab, 
        setTabData, 
        setTabSelectedIds, 
        setTabLoadingState,
        resetAllTabScrollTop,
        setTabScrollTop,
    } = useTQC2020_R01Store();
	const { unitList } = useCommonCodeStore();

    // 활성 탭에 따른 데이터/컬럼 매핑 - 생산실적 조회 규격
    const { columns, data } = useMemo(() => {
        switch (activeTab) {
            case 'item':
                return { columns: getItemProductColumns(unitList), data: tabData.item };
            case 'workplace':
                return { columns: getWorkplaceProductColumns(unitList), data: tabData.workplace };
            case 'date':
                return { columns: getDateProductColumns(unitList), data: tabData.date };
            default:
                return { columns: getDateProductColumns(unitList), data: tabData.date };
        }
    }, [activeTab, tabData.date, tabData.item, tabData.workplace]);

    // 뷰 타입 변환 함수 - 생산실적 조회 규격
    const getViewType = useCallback((tab: TabType): string => {
        switch (tab) {
            case 'date': return 'date';
            case 'item': return 'item';
            case 'workplace': return 'workplace';
            default: return 'date';
        }
    }, []);

    // 탭 이름 변환 함수 (파일명/메시지용)
    const getTabDisplayName = useCallback((tab: TabType): string => {
        switch (tab) {
            case 'date': return '일자별실적';
            case 'item': return '품목별실적';
            case 'workplace': return '공정별실적';
            default: return '일자별실적';
        }
    }, []);

    // 탭 변경 시 상태 초기화 
    const changeTabAndFetch = useCallback((tab: TabType, options: { clearData?: boolean; clearSelection?: boolean } = {}) => {
        const { clearData = true, clearSelection = true } = options;
        
        // 활성 탭 변경
        setActiveTab(tab);
        
        // 데이터 및 선택 상태 초기화
        if (clearData) {
            setTabData(tab, []);
        }
        if (clearSelection) {
            setTabSelectedIds(tab, []);
        }
    }, [setActiveTab, setTabData, setTabSelectedIds]);

    // 전체 탭 병렬 조회 
    const fetchData = useCallback(async () => {
        resetAllTabScrollTop();
        const tabs: TabType[] = ['date', 'item', 'workplace'];

        const { getTQC2020_R01Data } = await import('./Api_TQC2020_R01');

        // 각 탭별 응답 건수를 저장할 배열
        const tabCounts: { tab: TabType; count: number }[] = [];

        await Promise.all(tabs.map(async (tab) => {
            try {
                setTabLoadingState(tab, true);

                const viewType = getViewType(tab);                
                const res = await getTQC2020_R01Data(viewType, TQC2020_R01Filter);

                // 데이터 검증 및 중복 제거 (행 ID 기준) - 생산실적 규격
                if (res?.data?.data && Array.isArray(res.data.data)) {
                    const validData = res.data.data.filter((item: any) => item && typeof item === 'object');

                    const uniqueData = validData.filter((item: any, index: number, array: any[]) => {
                        let rowId = '';
                        switch (tab) {
                            case 'date':
                                rowId = `${item.compNm}-${item.inspectDt}-${item.workplaceNm}-${item.itemCd}-${item.inspectorEmpNm}-${item.badCaseNm}`;
                                break;
                            case 'item':
                                rowId = `${item.compNm}-${item.itemCd}-${item.workplaceNm}-${item.inspectDt}-${item.inspectorEmpNm}-${item.badCaseNm}`;
                                break;
                            case 'workplace':
                                rowId = `${item.compNm}-${item.workplaceNm}-${item.itemCd}-${item.inspectDt}-${item.inspectorEmpNm}-${item.badCaseNm}`;
                                break;
                        }

                        const isFirst = array.findIndex((i: any) => {
                            let compareId = '';
                            switch (tab) {
                                case 'date':
                                    compareId = `${i.compNm}-${i.inspectDt}-${i.workplaceNm}-${i.itemCd}-${i.inspectorEmpNm}-${i.badCaseNm}`;
                                    break;
                                case 'item':
                                    compareId = `${i.compNm}-${i.itemCd}-${i.workplaceNm}-${i.inspectDt}-${i.inspectorEmpNm}-${i.badCaseNm}`;
                                    break;
                                case 'workplace':
                                    compareId = `${i.compNm}-${i.workplaceNm}-${i.itemCd}-${i.inspectDt}-${i.inspectorEmpNm}-${i.badCaseNm}`;
                                    break;
                            }
                            return compareId === rowId;
                        }) === index;
                        return isFirst;
                    });

                    if (uniqueData.length > 0) {
                        setTabData(tab, uniqueData);
                        tabCounts.push({ tab, count: uniqueData.length });
                    } else {
                        console.warn(`${tab} 탭: 처리 후 유효한 데이터가 없습니다.`);
                        setTabData(tab, []);
                        tabCounts.push({ tab, count: 0 });
                    }
                } else {
                    console.warn(`${tab} 탭: 응답 데이터가 없거나 잘못된 형식입니다.`, res);
                    setTabData(tab, []);
                    tabCounts.push({ tab, count: 0 });
                }
            } catch (e) {
                console.error(`${tab} 탭 데이터 조회 실패:`, e);
                setTabData(tab, []);
                tabCounts.push({ tab, count: 0 });
            } finally {
                setTabLoadingState(tab, false);
            }
        }));
        
        // 각 탭별 건수 표시
        const totalCount = tabCounts.reduce((sum, item) => sum + item.count, 0);
        const tabDetails = tabCounts.map(item => `${getTabDisplayName(item.tab)}: ${item.count}건`).join(', ');

		ShowSuccessFetch(setMessage, `생산실적 조회가 완료되었습니다. (총 ${totalCount}건) `);
		setTimeout(() => setMessage(""), 5000);

    }, [getViewType, setTabData, setTabLoadingState, TQC2020_R01Filter, getTabDisplayName, setMessage, resetAllTabScrollTop]);

    // 개별 탭 조회 함수
    const fetchSingleTabData = useCallback(async (tab: TabType) => {
        setTabScrollTop(tab, null);
        try {
            setTabLoadingState(tab, true);

            const { getTQC2020_R01Data } = await import('./Api_TQC2020_R01');
            const viewType = getViewType(tab);
            const res: any = await getTQC2020_R01Data(viewType, TQC2020_R01Filter);

            // 데이터 검증 및 처리
            if (res?.data?.data && Array.isArray(res.data.data)) {
                const validData = res.data.data.filter((item: any) => item && typeof item === 'object');

                // 중복 제거 (행 ID 기준)
                const uniqueData = validData.filter((item: any, index: number, array: any[]) => {
                    let rowId = '';
                    switch (tab) {
                        case 'date':
                            rowId = `${item.compNm}-${item.productDt}-${item.workplaceNm}-${item.itemCd}-${item.workerEmpNm}`;
                            break;
                        case 'item':
                            rowId = `${item.compNm}-${item.itemCd}-${item.workplaceNm}-${item.productDt}-${item.workerEmpNm}`;
                            break;
                        case 'workplace':
                            rowId = `${item.compNm}-${item.workplaceNm}-${item.itemCd}-${item.productDt}-${item.workerEmpNm}`;
                            break;
                    }

                    const isFirst = array.findIndex((i: any) => {
                        let compareId = '';
                        switch (tab) {
                            case 'date':
                                compareId = `${i.compNm}-${i.productDt}-${i.workplaceNm}-${i.itemCd}-${i.workerEmpNm}`;
                                break;
                            case 'item':
                                compareId = `${i.compNm}-${i.itemCd}-${i.workplaceNm}-${i.productDt}-${i.workerEmpNm}`;
                                break;
                            case 'workplace':
                                compareId = `${i.compNm}-${i.workplaceNm}-${i.itemCd}-${i.productDt}-${i.workerEmpNm}`;
                                break;
                        }
                        return compareId === rowId;
                    }) === index;
                    return isFirst;
                });

                if (uniqueData.length > 0) {
                    setTabData(tab, uniqueData);

					ShowSuccessFetch(setMessage, `${getTabDisplayName(tab)} 조회가 완료되었습니다. (총 ${uniqueData.length}건)`);
					setTimeout(() => setMessage(""), 5000);
                } else {
                    console.warn(`${tab} 탭: 처리 후 유효한 데이터가 없습니다.`);
                    setTabData(tab, []);
                }
            } else {
                console.warn(`${tab} 탭: API 응답 데이터가 없거나 잘못된 형식입니다.`, res);
                setTabData(tab, []);
            }
        } catch (e) {
            console.error(`${tab} 탭 데이터 조회 실패:`, e);
            setTabData(tab, []);
            showWarning(`${getTabDisplayName(tab)} 조회 중 오류가 발생했습니다.`);
        } finally {
            setTabLoadingState(tab, false);
        }
    }, [getViewType, setTabData, setTabLoadingState, TQC2020_R01Filter, getTabDisplayName, setMessage, setTabScrollTop]);

    // 탭 변경 시 데이터 조회 처리 - 항상 조회
    const handleTabChange = useCallback(async (tab: TabType) => {
        try {
            // 1. 탭 변경 및 상태 초기화
            changeTabAndFetch(tab, { clearData: true, clearSelection: true });
            
            // 2. 활성 탭 항상 조회 (데이터가 있어도 최신 데이터로 갱신)
            await fetchSingleTabData(tab);
        } catch (error) {
            console.error('탭 변경 중 오류:', error);
        }
    }, [changeTabAndFetch, fetchSingleTabData]);

    // Excel 내보내기 핸들러
    const handleExcelClick = useCallback(async () => {
        try {
            const state = useTQC2020_R01Store.getState();
            const activeTab = state.activeTab;
            const filter = state.TQC2020_R01Filter;

            // 뷰 타입 변환
            const viewType = getViewType(activeTab);
            // API 호출
            const response = await exportTQC2020_R01Excel(viewType, { ...filter });

            // Base64 응답 처리
            if (response?.data?.data) {
                // 공통 유틸리티 사용
                downloadSettingExcel({ 
                    responseData: response.data.data, 
                    fileName: `생산실적조회_${getTabDisplayName(activeTab)}.xlsx`
                });
            } else {
                throw new Error('서버에서 올바른 엑셀 데이터를 받지 못했습니다.');
            }
            
        } catch (error) {
            console.error('Excel 내보내기 실패:', error);
            
            // 사용자에게 에러 알림
            if (error instanceof Error) {
                showWarning(`엑셀 내보내기 실패: ${error.message}`);
            } else {
                showWarning('엑셀 내보내기 중 오류가 발생했습니다.');
            }
        }
    }, [getViewType, getTabDisplayName]);

    return { 
        fetchData, 
        handleExcelClick,
        handleTabChange,
        columns,
        data
    };
};
